
import CryptoJS from 'crypto-js';
import Base64 from 'crypto-js/enc-base64';
import Utf8 from 'crypto-js/enc-utf8';
import Hex from 'crypto-js/enc-hex';
import { getSafeRandom } from '../../utils';
import { getGlobalStorage } from '../../storage';

import forge from 'node-forge';
const { jsbn, pki, util, md } = forge;

/**
 * 生成唯一标识
 */
function uuidv4() {
    return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = (getSafeRandom() * 16) | 0,
            v = c == 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * AES 算法
 */
class AES {
    constructor(props) {
        
        this.check();
        
        /**
         * 默认密钥, 固定密钥时可以使用
         */
        this.defaultKey = props.defaultKey || this.generateKey();

        /**
         * 采用hex方式处理
         */
        this.isHex = props.isHex || false;

        /**
         * 加密解密参数
         */
        this.cfg = {
            // 增强加密
            iv: props.iv || Base64.parse('ZGIyMTM5NTYxYzlmZTA2OA=='),
            mode: props.mode || CryptoJS.mode.CTR,
            padding: props.padding || CryptoJS.pad.NoPadding,
        };
    }

    // 检查组件是否完成初始化，主要针对国密场景
    check(callback) {
        if (typeof CryptoJS === 'undefined') {
            this.cipher = {};
            callback && callback();
            throw new Error('未找到通用算法CryptoJS的库');
        } else {
            this.cipher = CryptoJS;
            callback && callback();
        }
    }

    /**
     * 对称加密
     * @param {*} message  明文信息
     * @param {*} key 秘钥
     * @param {*} standard 标准
     * @returns  {string}
     */
    symmetricEncrypt(message = '', key = '', standard = 'AES') {
        key = key || this.defaultKey;
        let keyDigest = Base64.parse(key);
        let encrypted = CryptoJS[standard].encrypt(message, keyDigest, this.cfg);
        // 这里需要注意  先拿结果  然后再做base64 加密  encrypted.ciphertext 是个16进制字符串
        let ciphertext = Base64.stringify(
            (this.isHex ? Hex : Utf8).parse(encrypted.ciphertext.toString()),
        );
        // let ciphertext = Base64.stringify(Hex.parse(encrypted.ciphertext.toString()));
        return ciphertext;
    }

    /**
     * 对称解密
     * @param {*} message  密文信息
     * @param {*} key 秘钥
     * @param {*} standard 标准
     * @returns  {string}
     */
    symmetricDecrypt(message = '', key = '', standard = 'AES') {
        key = key || this.defaultKey;
        try {
            let keyDigest = Base64.parse(key);
            //  base64 解密  plaintext 只能是Hex编码的base64字节数组
            let plaintext = this.isHex
                ? message
                : Base64.stringify(Hex.parse(Base64.parse(message).toString(Utf8)));
            let decrypted = CryptoJS[standard].decrypt(plaintext, keyDigest, this.cfg);
            return decrypted.toString(Utf8);
        } catch (error) {
            let NCC_SYS_Switch = JSON.parse(localStorage.getItem('NCC_SYS_Switch')) || {};
            let rockin = NCC_SYS_Switch.aesKey;
            let cowboy = getGlobalStorage('localStorage', 'cowboy');
            let storeCipher = NCC_SYS_Switch.localStorage;
            let gzip = NCC_SYS_Switch.gzip;
            let estr = `解密错误，请找平台开发看下,或者记录当前数据, \r\nrockin: ${rockin},\r\ncowboy: ${cowboy},\r\ngzip: ${gzip},\r\nstoreCipher: ${storeCipher},\r\n cookie: ${document.cookie}\r\n`;
            estr += `原始字符：${message}\r\n\r\n错误日志：`;

            // download('前端日志文件' + new Date().getTime(), estr + error);
            console.error ? console.error(error) : console.log(error);
            console.log(message);
            throw new Error(error);
        }
    }

    /**
     * 生成密钥
     * @returns {string}
     */
    generateKey(type) {
        let text = uuidv4();
        return type !== 'base64' ? uuidv4() : (Base64.stringify(Utf8.parse(text)) || 'ZGIyMTM5NTYxYzlmZTA2OA==');
    }

    /**
     * 获取存储的密钥
     * @returns {string}
     */
    getSecretKey() {
        return this.cipher?.getSecretKey();
    }

    /**
     * 非对称加密
     * @param {string} message 
     * @param {string} publicKey 
     * @returns {string}
     */
    asymmetricEncrypt(message, publicKey) {
        let encodeBytes = publicKey.encrypt(
            message,
            "RSA-OAEP",
            {
                md: md.sha256.create(),
                mgf1: { md: md.sha256.create() },
            },
        );
        let encodeHex = util.binary.hex.encode(encodeBytes);
        return encodeHex;
    }

    /**
     * 非对称解密
     * @param {string} message 
     * @param {string} publicKey 
     * @returns {string}
     */
    asymmetricDecrypt(message, publicKey) {
        return '';
    }

    /**
     * 生成公钥
     */
    generatePublicKey() {
        return '';
    }

    /**
     * 获取公钥
     * @returns {string}
     */
    getPublicKey(exponent, modulus) {
        if (!exponent || !modulus) {
            console.error('缺少生成密钥的参数！');
            return '';
        }
        let exponentBigInteger = new jsbn.BigInteger(exponent, 16);
        let modulusBigInteger = new jsbn.BigInteger(modulus, 16);
        let publicKey = pki.rsa.setPublicKey(modulusBigInteger, exponentBigInteger);
        return publicKey;
    }

    // 加签
    sign(salt, data) {
        return CryptoJS.enc.Base64.stringify(CryptoJS.SHA256(salt + data));
    }
}

export default AES;